import api from './config';

/**
 * API para gestionar prestaciones laborales
 * Usa la instancia `api` configurada (incluye x-company-id y Authorization automáticamente)
 */
export const prestacionesAPI = {
  /**
   * Obtener todas las prestaciones
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.companyId) params.append('companyId', filters.companyId);
    if (filters.empleadoId) params.append('empleadoId', filters.empleadoId);
    if (filters.estado) params.append('estado', filters.estado);
    const response = await api.get(`/prestaciones?${params.toString()}`);
    return response.data;
  },

  /**
   * Obtener una prestación por ID
   */
  getById: async (id) => {
    const response = await api.get(`/prestaciones/${id}`);
    return response.data;
  },

  /**
   * Calcular prestaciones en el backend (preview, sin guardar).
   */
  calcular: async ({ employeeId, fechaIngresoOverride, fechaSalidaOverride, tomoVacaciones, incluirPreaviso, incluirCesantia, horasExtrasPendientes, comisionesPendientes }) => {
    const response = await api.post('/prestaciones/calcular', {
      employeeId, fechaIngresoOverride, fechaSalidaOverride,
      tomoVacaciones, incluirPreaviso, incluirCesantia,
      horasExtrasPendientes, comisionesPendientes,
    });
    return response.data.data;
  },

  /**
   * Calcular prestaciones para múltiples empleados (departamento / organización).
   */
  calcularMultiple: async ({ employeeIds, fechaIngresoOverride = null, fechaSalidaOverride = null }) => {
    const response = await api.post('/prestaciones/calcular-multiple', {
      employeeIds, fechaIngresoOverride, fechaSalidaOverride,
    });
    return response.data.data;
  },

  /**
   * Crear (guardar) nueva prestación.
   */
  create: async ({ employeeId, fechaIngresoOverride, fechaSalidaOverride, tomoVacaciones, incluirPreaviso, incluirCesantia, horasExtrasPendientes, comisionesPendientes }) => {
    const response = await api.post('/prestaciones', {
      employeeId, fechaIngresoOverride, fechaSalidaOverride,
      tomoVacaciones, incluirPreaviso, incluirCesantia,
      horasExtrasPendientes, comisionesPendientes,
    });
    return response.data;
  },

  /**
   * Actualizar prestación existente
   */
  update: async (id, updates) => {
    const response = await api.put(`/prestaciones/${id}`, updates);
    return response.data;
  },

  /**
   * Eliminar prestación
   */
  delete: async (id) => {
    const response = await api.delete(`/prestaciones/${id}`);
    return response.data;
  },

  /**
   * Marcar prestación como pagada
   */
  markAsPaid: async (id, paymentData = {}) => {
    const response = await api.patch(`/prestaciones/${id}/marcar-pagado`, {
      fechaPago: paymentData.fechaPago || new Date().toISOString(),
      metodoPago: paymentData.metodoPago || 'transferencia',
      referencia: paymentData.referencia || ''
    });
    return response.data;
  },

  /**
   * Obtener cotizable histórico de todos los empleados para un período
   */
  getSalariosPeriodo: async (companyId, periodo) => {
    try {
      const response = await api.get(`/prestaciones/salarios-periodo/${companyId}/${periodo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching salarios periodo:', error);
      return {};
    }
  },

  getSalarioMesAnterior: async (companyId, employeeId) => {
    try {
      const response = await api.get(`/prestaciones/salario-mes-anterior/${companyId}/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching salario mes anterior:', error);
      return { salarioCotizable: null, salarioPromedio12: null };
    }
  },

  /**
   * Obtener estadísticas de prestaciones
   */
  getStats: async (companyId) => {
    const response = await api.get(`/prestaciones/stats?companyId=${companyId}`);
    return response.data;
  }
};

export default prestacionesAPI;
